<template>
  <div class="location-edit">
    <div class="row no-gutters">
      <div class="col-md-4 px-3 pb-3">
        <h4>Edit Project Location</h4>
        <b-form @submit="clickSubmit()" @reset="undo()" v-if="newLatLng && isManager">
          <b-form-group id="input-group-1" label="Name" label-for="input-1">
            <b-form-input id="input-1" v-model="name" placeholder="Enter Name" required name="name" v-validate="{
              required: true,
              min: 5,
              max: 100,
            }" :class="{
              'is-invalid': errors.has(`name`),
            }" aria-describedby="name-live-feedback"></b-form-input>
            <b-form-invalid-feedback id="name-live-feedback" v-if="errors.has('name')">
              {{ errors.first("name") }}
            </b-form-invalid-feedback>
          </b-form-group>
          <div class="row">
            <b-form-group id="input-group-1" class="col-6" label="Latitude" label-for="input-1">
              <b-form-input id="input-1" v-model.number="newLatLng.lat" placeholder="Enter latitude" type="number"
                :number="true" name="latitude" :step="0.0005" v-validate="{
                  required: true,
                  decimal: 7,
                  min_value: -180,
                  max_value: 180,
                }" :class="{
                  'is-invalid': errors.has(`latitude`),
                }" aria-describedby="latitude-live-feedback"></b-form-input>
              <b-form-invalid-feedback id="latitude-live-feedback" v-if="errors.has('latitude')">
                {{ errors.first("latitude") }}
              </b-form-invalid-feedback>
            </b-form-group>
            <b-form-group id="input-group-1" label="Longitude" label-for="input-1" class="col-6">
              <b-form-input id="input-1" v-model.number="newLatLng.lng" placeholder="Enter Longitude" type="number"
                :number="true" name="longitude" :step="0.0005" v-validate="{
                  required: true,
                  decimal: 7,
                  min_value: -180,
                  max_value: 180,
                }" :class="{
                  'is-invalid': errors.has(`longitude`),
                }" aria-describedby="longitude-live-feedback"></b-form-input>
              <b-form-invalid-feedback id="longitude-live-feedback" v-if="errors.has('longitude')">
                {{ errors.first("longitude") }}
              </b-form-invalid-feedback>
            </b-form-group>
          </div>
          <div class="row mb-3" v-if="observationsTotal > 0 ">
            <div class="col-md-12">
              <b-form-checkbox v-model="updateObservations" name="updateObservations">
                Update all existing observations to these coordinates
              </b-form-checkbox>

            </div>
          </div>
          <div>
            <span v-if="observationsTotal > 0 || observationsIsLoading">
              This edit <b v-if="updateObservations">will</b><b v-else>will not</b> affect the locations of the
              <b-link :to="{
                name: 'LocationObservations',
                params: { id: $route.params.id },
              }">
                <span class="font-weight-bold" v-if="!observationsIsLoading">{{
                  observationsTotal
                  }}</span><i v-else class="fa fa-spinner fa-spin mx-2"></i>
                observation<template v-if="observationsTotal !== 1">s</template> already submitted.
              </b-link>
            </span>
            <span>
              This updated location will be used for any future observations made through
              <b-link :to="{
                name: 'LocationAssignments',
                params: { id: $route.params.id },
              }">
                <span class="font-weight-bold" v-if="!assignmentsIsLoading">{{
                  assignmentsTotal
                  }}</span><i v-else class="fa fa-spinner fa-spin mx-2"></i> location
                assignment<template v-if="assignmentsTotal !== 1">s</template>.
              </b-link>
            </span>
          </div>

          <div class="row mb-3">
            <div class="col-md-6">
              <div class="btn btn-block btn-success mt-2" @click="clickSave()">
                <span v-if="editIsLoading">
                  <i class="fa fa-spinner fa-spin"></i> Saving...
                </span>
                <span v-else> <i class="fas fa-save"></i> Save </span>
              </div>
            </div>
            <div class="col-md-6">
              <div class="btn btn-block btn-secondary mt-2" @click="undo()">
                <i class="fas fa-undo"></i> Undo
              </div>
            </div>
          </div>
        </b-form>
        <div v-else-if="!isManager">
          <p>You do not have permission to edit this location. Please contact your project manager for assistance.</p>
        </div>
        <b-modal id="confirm-update-observations" title="Confirm Update Observations">
          <p>Are you sure you want to update the <b>{{ observationsTotal }} observations</b> coordinates? This cannot be undone.</p>
          <template #modal-footer>
            <b-button class="btn btn-warning" @click="$bvModal.hide('confirm-update-observations')">Cancel</b-button>
            <b-button class="btn btn-success" @click="clickSubmit()">Yes, update observations</b-button>
          </template>
        </b-modal>

      </div>
      <div class="col-md-8" v-if="newLatLng">
        <LMap class="location-map" :zoom="map.zoom" :center="map.center" @update:center="centerUpdate" ref="leaflet"
          @ready="addBasemapLayer(BASEMAP_ACTIVE)">
          <LLayerGroup>
            <LMarker :lat-lng="newLatLng" :draggable="true" @moveend="dragendNew">
              <MarkerPin></MarkerPin>
              <LTooltip content="Drag marker to move" />
            </LMarker>

            <LMarker @click="moveHere()" :lat-lng="map.center" :draggable="false" v-if="newLatLng !== map.center">
              <MarkerCrosshairs />
              <LTooltip>
                <div>Center of Map</div>
                <div>Click to move here</div>
              </LTooltip>
            </LMarker>
            <LPolyline :lat-lngs="[origionalLatLng, newLatLng]" :color="'#237cc9'" :weight="3"></LPolyline>
          </LLayerGroup>
        </LMap>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import {
  LMap,
  LTileLayer,
  LLayerGroup,
  LMarker,
  LPopup,
  LTooltip,
  LRectangle,
  LCircle,
  LPolyline,
  LControlZoom,
} from "vue2-leaflet";
import MarkerCrosshairs from "../common/map/MarkerCrosshairs.vue";
import MarkerPin from "../common/map/MarkerPin.vue";

export default {
  name: "LocationEdit",
  components: {
    LMap,
    LTileLayer,
    LLayerGroup,
    LMarker,
    LPopup,
    LTooltip,
    LRectangle,
    LCircle,
    LPolyline,
    LControlZoom,
    MarkerCrosshairs,
    MarkerPin,
  },
  data() {
    return {
      map: {
        zoom: 12,
        center: null,
        url: "https://services.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}",
        attribution:
          '&copy; <a target="_blank" rel="noopener noreferrer" href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      },

      newLatLng: null,
      origionalLatLng: null,
      name: null,
      updateObservations: false,
    };
  },
  mounted() {
    // Fetch observations when the component is mounted
    this.fetchObservations();
    this.fetchAssignments();
    if (this.location && this.location.lngLat) {
      const lngLat = {
        lng: Number(this.location.lngLat.lng.toString().replace(/(\.\d{7})\d+/, "$1")),
        lat: Number(this.location.lngLat.lat.toString().replace(/(\.\d{7})\d+/, "$1")),
      };
      this.name = this.location.name;
      this.map.center = { ...lngLat };
      this.newLatLng = { ...lngLat };
      this.origionalLatLng = { ...lngLat };
    }
  },
  computed: {
    ...mapGetters({
      location: "location/details",
      isManager: "location/isManager",
      observationsTotal: "location/observationsTotal",
      assignmentsTotal: "location/assignmentsTotal",
      observationsIsLoading: "location/observationsIsLoading",
      assignmentsIsLoading: "location/assignmentsIsLoading",
      editIsLoading: "location/editIsLoading",
      BASEMAP_ACTIVE: "map/BASEMAP_ACTIVE",
    }),
  },
  methods: {
    // Component methods
    ...mapActions({
      fetchObservations: "location/fetchObservations",
      fetchAssignments: "location/fetchAssignments",
      updateLocation: "location/updateLocation",
    }),
    centerUpdate(center) {
      this.map.center = center;
    },
    moveHere() {
      const lngLat = {
        lng: Number(this.map.center.lng.toString().replace(/(\.\d{7})\d+/, "$1")),
        lat: Number(this.map.center.lat.toString().replace(/(\.\d{7})\d+/, "$1")),
      };
      this.newLatLng = lngLat;
    },
    dragendNew(e) {
      const lngLat = {
        lng: Number(e.sourceTarget._latlng.lng.toString().replace(/(\.\d{7})\d+/, "$1")),
        lat: Number(e.sourceTarget._latlng.lat.toString().replace(/(\.\d{7})\d+/, "$1")),
      };
      this.newLatLng = lngLat;
    },
    clickSave() {
      if(this.updateObservations){
        this.$bvModal.show('confirm-update-observations')
      } else {
        this.clickSubmit();
      }
    },
    clickSubmit() {
      const lngLat = {
        lng: Number(this.newLatLng.lng.toString().replace(/(\.\d{7})\d+/, "$1")),
        lat: Number(this.newLatLng.lat.toString().replace(/(\.\d{7})\d+/, "$1")),
      };
      console.log("Submitting location update with the following data:", {
        name: this.name,
        lngLat: lngLat,
        updateObservations: this.updateObservations,
      });
      this.updateLocation({
        name: this.name,
        lngLat: lngLat,
        updateObservations: this.updateObservations,
      });
      this.$bvModal.hide('confirm-update-observations')
    },
    undo() {
      this.newLatLng = this.origionalLatLng;
      this.map.center = this.origionalLatLng;
      this.name = this.location.name;
    },
    addBasemapLayer(newLayer, oldLayer) {
      const map = this.$refs.leaflet && this.$refs.leaflet.mapObject;
      if (!map || !newLayer) return;
      if (oldLayer) {
        try {
          map.removeLayer(oldLayer);
        } catch (e) { }
      }
      newLayer.addTo(map);
      map.invalidateSize();
    },
  },
  watch: {
    BASEMAP_ACTIVE(newLayer, oldLayer) {
      this.addBasemapLayer(newLayer, oldLayer);
    },
    location: {
      handler(newLocation) {
        const lngLat = {
          lng: Number(this.location.lngLat.lng.toString().replace(/(\.\d{7})\d+/, "$1")),
          lat: Number(this.location.lngLat.lat.toString().replace(/(\.\d{7})\d+/, "$1")),
        };
        if (newLocation && newLocation.lngLat) {
          this.name = this.location.name;
          this.map.center = lngLat;
          this.newLatLng = lngLat;
          this.origionalLatLng = lngLat;
        }
      },
    },
  },
};
</script>

<style scoped>
.location-map {
  width: 100%;
  height: 80vh;
}
</style>
